/* eslint-disable no-unused-vars */
import { useMemo, useEffect } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import { useAuth } from '../../../Hooks/useAuth';
import { useDeliveryUtilizationReport } from './DeliveryUtilizationReportProvider';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { MIS_DEL_DETAILS} from '../../../Api/resources/endPoint';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import './DeliveryUtilizationReport.scss';
import dayjs from 'dayjs';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';

const getBaseURL = () => {
  const service = serviceName.RESELLER_SERVICE;
  let url = '';
  let port = '';
  if (process.env.NODE_ENV === 'production') {
    url = appendServiceNameToURL(service);
    return process.env.REACT_APP_API_URL + url + '/api' + MIS_DEL_DETAILS;
  } else {
    url = appendServiceNameToURL(service);
    port = appendServicePortToURL(service);
    return process.env.REACT_APP_API_URL + `:${port}` + url + '/api' + MIS_DEL_DETAILS;
  }
};

const baseURL = getBaseURL();

const DeliveryUtilizationReportTable = () => {
  const { userProfile } = useAuth();
  const { fromdate, todate, setTableData, refreshTrigger, setSummaryDetails, setLoading }
  = useDeliveryUtilizationReport();
  // Add this useEffect in DeliveryUtilizationReportTable.js

  useEffect(() => {
    const applyNumericAlignment = () => {
      const table = document.querySelector('.table-utility .p-datatable');
      if (!table) return;

      // Define the numeric field names from your API
      const numericFields = ['expected_effort', 'total_efforts', 'hours_billable', 'utilization_percentage'];

      // Get all headers to find positions of numeric columns
      const headers = table.querySelectorAll('.p-datatable-thead th');
      const numericColumnIndices = [];

      headers.forEach((header, index) => {
        const headerText = header.textContent.toLowerCase();

        // Check if header indicates a numeric column
        if (headerText.includes('expected effort')
          || headerText.includes('total logged effort')
          || headerText.includes('billable effort')
          || headerText.includes('utilization')) {
          numericColumnIndices.push(index + 1); // nth-child is 1-based
        }
      });

      // Clear previous numeric alignments
      const allCells = table.querySelectorAll('.p-datatable-tbody tr td, .p-datatable-thead tr th');
      allCells.forEach(cell => {
        cell.classList.remove('numeric-aligned');
        cell.style.textAlign = '';
        cell.style.paddingRight = '';

        const div = cell.querySelector('div');
        if (div) {
          div.style.textAlign = '';
          div.style.paddingRight = '';
        }
      });

      // Apply alignment to numeric columns
      numericColumnIndices.forEach(index => {
      // Body cells
        const bodyCells = table.querySelectorAll(`.p-datatable-tbody tr td:nth-child(${index})`);
        bodyCells.forEach(cell => {
          cell.style.textAlign = 'right';
          cell.style.paddingRight = '30px';
          cell.classList.add('numeric-aligned');

          const div = cell.querySelector('div');
          if (div) {
            div.style.textAlign = 'right';
            div.style.paddingRight = '22px';
          }
        });

        // Header cells
        const headerCells = table.querySelectorAll(`.p-datatable-thead tr th:nth-child(${index})`);
        headerCells.forEach(header => {
          header.style.textAlign = 'right';
          header.classList.add('numeric-aligned');

          const headerContent = header.querySelector('.p-column-header-content');
          if (headerContent) {
            headerContent.style.justifyContent = 'flex-end';
          }
        });
      });
    };

    // Apply immediately
    const timer = setTimeout(applyNumericAlignment, 300);

    // Watch for DOM changes (pinning, filtering, sorting)
    const observer = new MutationObserver((mutations) => {
      let shouldReapply = false;

      mutations.forEach(mutation => {
      // Check if table structure changed
        if (mutation.type === 'childList'
          || (mutation.type === 'attributes'
           && ['class', 'style', 'data-p-frozen-column'].includes(mutation.attributeName))) {
          shouldReapply = true;
        }
      });

      if (shouldReapply) {
        setTimeout(applyNumericAlignment, 100);
      }
    });

    const tableContainer = document.querySelector('.table-utility');
    if (tableContainer) {
      observer.observe(tableContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'data-p-frozen-column'],
      });
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [refreshTrigger]);

  const handleApiResponse = (response) => {
    setLoading(false);
    setSummaryDetails({
      utilizationColumns: response.data.utilizationColumns || [],
      utilizationTableData: response.data.utilizationData || [],
    });
  };

  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL,
        userInfo: {
          emailId: userProfile.email,
          permissions: userProfile.permissions,
          roleDetails: userProfile.roleDetails,
          employeeId: userProfile.employeeId,
          fromdate: dayjs(fromdate).format('YYYY-MM-DD'),
          todate: dayjs(todate).format('YYYY-MM-DD'),
        },
        apiEndpoints: {
          getAllViews: { endpoint: 'views', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: 'view', enabled: false },
          deleteView: { endpoint: 'view-delete', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: () => getExportFileName('Delivery Utilization Report'),
            sheetName: () =>
              getExportFileNameWithDate('Delivery Utilization Report'),
          },
        },
        handler: {
          handleFilteredApiResponse: handleApiResponse,
        },
        headers: {
          token: userProfile.access_token,
        },
        showToolBar: true,
        showPagination: true,
        toolBarSettings: {
          showAddNew: false,
          showDownloadExcel: true,
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showSaveView: false,
          showExpandCollapse: false,
          showReset: true,
          additionalElements: [],
        },
      },
      tableSettings: {
        size: 'large',
        pageSize: 10,
        showPageSizeSelector: false,
        loaderSkeleton: {
          rows: 3,
          bodyCells: 2,
          columns: 4,
        },
        showExpandCollapse: false,
        resizableColumns: false,
        tableScrollHeight: '400px',
        maxFrozenColumns: {
          desktop: 4,
          tablet: 2,
          mobile: 1,
        },
      },
      onDataFetch: (data) => {
        setTableData(data?.tableData || []);
      },
    }),
    [baseURL, userProfile, fromdate, todate, refreshTrigger],
  );
  return (
    <div className="du-outer-layer">
      <div className="du">
        <div className="du-du-title"></div>
        <div className="table-utility">
          <TableUtility
            key={`table-${refreshTrigger}`}
            configuration={configuration}
            pageSize={10}
          />
        </div>
      </div>
    </div>
  );
};

export default DeliveryUtilizationReportTable;
